import { AudioBed } from "../../game/audio";

/**
 * Pulse v5(c) — 3-tap auto-calibration wizard.
 *
 * Replaces the per-game single tap_offset_ms slider with a guided
 * 3-tap warm-up. The wizard fires three downbeats at a fixed BPM, the
 * player taps when they hear/see each one, and the median of
 * (tap_time - target_time) is the calibrated input-lag offset.
 *
 * Why median over mean: input-lag is dominated by an OS/audio-output
 * step plus a personal latency that's roughly normal; median tolerates
 * one stray late/early tap from a startled player without skewing.
 *
 * The wizard renders a fullscreen DOM overlay (no Three.js touch) so it
 * can run before the engine starts. AudioBed is the same one used by
 * the main game — caller passes it in so the user-gesture audio unlock
 * is shared across both surfaces.
 *
 * Returns the offset in ms (clamped to ±150). Resolves to `null` if
 * the user dismisses the wizard before the third beat. The advanced
 * manual slider in main-pulse.ts writes to the same storage key, so
 * the wizard and the slider stay in sync.
 */
const STORAGE_KEY = "pulse.tap_offset_ms";
/** ±150 ms covers virtually all real-world input chains (BT audio ~80ms,
 *  USB-audio ~5ms, Bluetooth controller ~30ms, HDMI display ~20ms). */
const OFFSET_CLAMP_MS = 150;

const LEAD_MS = 1500;
const BEAT_INTERVAL_MS = 700;
const N_BEATS = 3;
const POST_BEAT_GRACE_MS = 700;
/** Reject taps further than this from the nearest beat — usually a
 *  panic-tap during lead-in or a stray finger. */
const TAP_MATCH_WINDOW_MS = 350;

export function loadTapOffsetMs(): number {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return 0;
    const n = Number(raw);
    if (!Number.isFinite(n)) return 0;
    return Math.max(-OFFSET_CLAMP_MS, Math.min(OFFSET_CLAMP_MS, n));
  } catch {
    return 0;
  }
}

export function saveTapOffsetMs(ms: number): void {
  const clamped = Math.max(-OFFSET_CLAMP_MS, Math.min(OFFSET_CLAMP_MS, Math.round(ms)));
  try {
    localStorage.setItem(STORAGE_KEY, String(clamped));
  } catch {
    /* private mode */
  }
}

export interface CalibrationResult {
  /** Offset in ms (clamped to ±150). Positive = subtract from raw tap. */
  offsetMs: number;
  /** Per-beat deltas in the order they were captured (tap - target). */
  deltas: number[];
}

export function runCalibrationWizard(audio: AudioBed): Promise<CalibrationResult | null> {
  return new Promise((resolve) => {
    audio.arm();

    const overlay = document.createElement("div");
    overlay.className = "pulse-cal-overlay";
    overlay.innerHTML = `
      <div class="pulse-cal-card">
        <div class="pulse-cal-title">Calibrate</div>
        <div class="pulse-cal-sub">Tap the ring on each pulse — 3 beats incoming.</div>
        <div class="pulse-cal-stage">
          <div class="pulse-cal-target" id="pulse-cal-target"></div>
          <div class="pulse-cal-ring" id="pulse-cal-ring"></div>
        </div>
        <div class="pulse-cal-counter" id="pulse-cal-counter">— / 3</div>
        <button type="button" class="btn ghost" id="pulse-cal-cancel">Cancel</button>
      </div>
    `;
    document.body.appendChild(overlay);

    const target = overlay.querySelector<HTMLDivElement>("#pulse-cal-target")!;
    const ring = overlay.querySelector<HTMLDivElement>("#pulse-cal-ring")!;
    const counter = overlay.querySelector<HTMLDivElement>("#pulse-cal-counter")!;
    const cancel = overlay.querySelector<HTMLButtonElement>("#pulse-cal-cancel")!;

    const beatTimes: number[] = [];
    for (let i = 0; i < N_BEATS; i++) beatTimes.push(LEAD_MS + i * BEAT_INTERVAL_MS);
    const totalMs = beatTimes[N_BEATS - 1] + POST_BEAT_GRACE_MS;

    const startedAt = performance.now();
    const taps: number[] = [];
    let done = false;
    let raf = 0;
    const beatScheduled: boolean[] = beatTimes.map(() => false);

    function cleanup() {
      cancelAnimationFrame(raf);
      window.removeEventListener("keydown", onKey);
      overlay.removeEventListener("pointerdown", onPointer);
      cancel.removeEventListener("click", onCancel);
      overlay.remove();
    }

    function finish() {
      if (done) return;
      done = true;
      // Match each beat to the tap nearest to its target time, within
      // TAP_MATCH_WINDOW_MS. A beat with no tap in its window contributes
      // no delta. Median of whatever survives.
      const deltas: number[] = [];
      const used = new Set<number>();
      for (const bt of beatTimes) {
        let bestIdx = -1;
        let best = Infinity;
        for (let i = 0; i < taps.length; i++) {
          if (used.has(i)) continue;
          const d = Math.abs(taps[i] - bt);
          if (d < best && d <= TAP_MATCH_WINDOW_MS) {
            best = d;
            bestIdx = i;
          }
        }
        if (bestIdx >= 0) {
          used.add(bestIdx);
          deltas.push(taps[bestIdx] - bt);
        }
      }
      cleanup();
      if (deltas.length === 0) {
        resolve(null);
        return;
      }
      const sorted = [...deltas].sort((a, b) => a - b);
      const mid = Math.floor(sorted.length / 2);
      const median = sorted.length % 2 === 0
        ? (sorted[mid - 1] + sorted[mid]) / 2
        : sorted[mid];
      const offsetMs = Math.max(-OFFSET_CLAMP_MS, Math.min(OFFSET_CLAMP_MS, Math.round(median)));
      saveTapOffsetMs(offsetMs);
      resolve({ offsetMs, deltas });
    }

    function onCancel() {
      if (done) return;
      done = true;
      cleanup();
      resolve(null);
    }

    function recordTap() {
      if (done) return;
      taps.push(performance.now() - startedAt);
    }

    function onPointer(e: PointerEvent) {
      if ((e.target as HTMLElement)?.closest("#pulse-cal-cancel")) return;
      recordTap();
    }

    function onKey(e: KeyboardEvent) {
      if (e.code === "Space") { e.preventDefault(); recordTap(); }
      if (e.code === "Escape") onCancel();
    }

    function tick() {
      if (done) return;
      const now = performance.now() - startedAt;
      // Schedule chime + visual pulse on each beat.
      for (let i = 0; i < beatTimes.length; i++) {
        if (!beatScheduled[i] && now >= beatTimes[i]) {
          beatScheduled[i] = true;
          audio.chime(12, 0);
          counter.textContent = `${i + 1} / 3`;
          target.classList.remove("pulse");
          // Force reflow so the animation restarts cleanly.
          void target.offsetWidth;
          target.classList.add("pulse");
        }
      }
      // Ring travel: scale from 2.6 → 1.0 over LEAD_MS into the first beat,
      // then jump to the next beat's lead-in after each fire. The ring is
      // a single element that respawns each beat.
      const nextBeatI = beatScheduled.findIndex((b) => !b);
      const nextBeatT = nextBeatI === -1 ? null : beatTimes[nextBeatI];
      if (nextBeatT === null) {
        ring.style.opacity = "0";
      } else {
        const leadIn = nextBeatI === 0 ? LEAD_MS : BEAT_INTERVAL_MS;
        const prevBeatT = nextBeatI === 0 ? 0 : beatTimes[nextBeatI - 1];
        const inWindow = now >= prevBeatT && now <= nextBeatT;
        if (inWindow) {
          const u = (nextBeatT - now) / leadIn;
          const scale = 1.0 + Math.max(0, u) * 1.8;
          ring.style.transform = `translate(-50%, -50%) scale(${scale.toFixed(3)})`;
          ring.style.opacity = String(Math.max(0.15, 1 - u * 0.5));
        }
      }
      if (now >= totalMs) {
        finish();
        return;
      }
      raf = requestAnimationFrame(tick);
    }

    overlay.addEventListener("pointerdown", onPointer);
    window.addEventListener("keydown", onKey);
    cancel.addEventListener("click", onCancel);
    raf = requestAnimationFrame(tick);
  });
}
